import { Box, Button, InputAdornment, Stack, TextField, Typography, useTheme } from '@mui/material'
import { Moment } from 'moment'
import React, { FC, KeyboardEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RoundedSearchIcon } from '../../../../components/icons'
import { StyledCard } from '../../../../components/StyledCard'
import { FederalActions, RootState, SmallBusinessSliceActions } from '../../../../store'
import { FormsActions, FormsSelector } from '../../../../store/forms'
import { PrivateFoundationsActions } from '../../../../store/privateFoundations'
import { AgencyFilter, DateFilter, PhaseFilter, StatusFilter } from '../SearchFilters'

interface Filters {
  agencies: string[]
  phases: string[]
  statuses: string[]
  deadline: {
    startDate: Moment | null
    endDate: Moment | null
  }
}

interface ISearchFormProps {
  formId: string
  heading: string
  subHeading?: string
  loading: boolean
  onSearch: () => void
  filters: Filters
  handleFiltersChange: (filters: any) => void
}

export const SearchForm: FC<ISearchFormProps> = ({
  formId,
  filters,
  heading,
  subHeading,
  loading,
  onSearch,
  handleFiltersChange,
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const formValues = useSelector((state: RootState) => FormsSelector.getFormValues(state, formId))

  const { value = '' } = formValues

  const { agencies, statuses, phases, deadline } = filters

  const handleStartDateChange = (newStartDate: any) => {
    if (!newStartDate && !deadline.endDate) {
      return
    }

    handleFiltersChange({
      dates: {
        startDate: newStartDate,
        endDate: newStartDate ? deadline.endDate : null,
      },
    })
  }

  const handleEndDateChange = (newEndDate: any) => {
    if (!newEndDate && !deadline.startDate) {
      return
    }

    handleFiltersChange({
      dates: {
        startDate: newEndDate ? deadline.startDate : null,
        endDate: newEndDate,
      },
    })
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    if (!value && formId === 'global') {
      dispatch(FederalActions.resetGrants())
      dispatch(SmallBusinessSliceActions.resetGrants())
      dispatch(PrivateFoundationsActions.resetPFs())
    }

    dispatch(FormsActions.setValue({ id: formId, value }))

    if (formId === 'global') {
      dispatch(FormsActions.setValue({ id: 'sbir', value }))
      dispatch(FormsActions.setValue({ id: 'federal', value }))
      dispatch(FormsActions.setValue({ id: 'privateFoundation', value }))
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch()
    }
  }

  const handleSearch = () => {
    onSearch()
  }

  return (
    <Box mb={3}>
      <StyledCard>
        <Typography
          mb={1}
          color="secondary"
          fontSize="1rem"
          fontWeight={theme.typography.fontWeightBold}
        >
          {heading}
        </Typography>

        {subHeading && (
          <Typography
            fontSize="0.875rem"
            color="secondary.light"
            fontWeight={theme.typography.fontWeightRegular}
          >
            {subHeading}
          </Typography>
        )}

        <Stack mt={3} gap={2} direction="row" alignItems="center">
          <TextField
            type="text"
            value={value}
            disabled={loading}
            autoComplete="off"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Search for grants"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <RoundedSearchIcon />
                </InputAdornment>
              ),
              sx: {
                height: '40px',
                width: '400px',
                borderRadius: '8px',
                background: 'white',
                borderColor: theme.palette.grey[400],
              },
            }}
          />

          <Button
            color="primary"
            disableElevation
            variant="contained"
            disabled={loading}
            sx={{
              height: '38px',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Stack>

        {formId !== 'privateFoundation' && (
          <Stack mt={2} direction="row" alignItems="center" gap={2} flexWrap="wrap">
            <AgencyFilter
              selectedAgencies={agencies}
              handleAgencyChange={(agencies: any) =>  handleFiltersChange({ agencies })}
            />
            <PhaseFilter
              selectedPhases={phases}
              handlePhaseChange={(phases: any) => handleFiltersChange({ phases })}
            />
            <StatusFilter
              selectedStatuses={statuses}
              handleStatusChange={(statuses: any) => handleFiltersChange({ statuses })}
            />
            <DateFilter
              startDate={deadline.startDate}
              endDate={deadline.endDate}
              setStartDate={handleStartDateChange}
              setEndDate={handleEndDateChange}
            />
          </Stack>
        )}
      </StyledCard>
    </Box>
  )
}
